import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { authService } from '@/services/auth.service';
import { useNavigate } from 'react-router-dom';

export function RegisterForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await authService.signUp(email, password);
      
      toast({
        title: "Account created!",
        description: "Verification email sent - please check your inbox",
        duration: 6000,
        variant: "default"
      });
      
      navigate('/auth/verify', { 
        state: { email: result.user.email }
      });
    } catch (error: any) {
      toast({
        title: 'Sign up failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      const result = await authService.signInWithGoogle();
      if (result?.url) {
        window.location.href = result.url;
      }
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') {
        return;
      }
      
      console.error('Error signing up with Google:', error);
      toast({
        title: 'Authentication Error',
        description: error.message || 'Failed to sign up with Google. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <h2 className="text-sm font-medium mb-2">Email</h2>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="transition-all duration-200 focus:ring-2 focus:ring-indigo-500/20"
          />
        </div>
        
        <div>
          <h2 className="text-sm font-medium mb-2">Password</h2>
          <Input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="transition-all duration-200 focus:ring-2 focus:ring-indigo-500/20"
          />
        </div>

        <div>
          <h2 className="text-sm font-medium mb-2">Confirm Password</h2>
          <Input
            type={showPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="transition-all duration-200 focus:ring-2 focus:ring-indigo-500/20"
          />
        </div>

        <div className="flex items-center mt-6">
          <input
            type="checkbox"
            id="showPassword"
            checked={showPassword}
            onChange={(e) => setShowPassword(e.target.checked)}
            className="rounded border-gray-300"
          />
          <label htmlFor="showPassword" className="ml-2 text-sm text-gray-600">
            Show Password
          </label>
        </div>

        <Button
          type="submit"
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white mt-6"
        >
          Create Account
        </Button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-4 text-gray-500 font-medium tracking-wider">
            OR CONTINUE WITH
          </span>
        </div>
      </div>

      <Button
        variant="outline"
        onClick={handleGoogleSignUp}
        className="w-full border-gray-300 hover:bg-gray-50 transition-colors"
      >
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"
          />
        </svg>
        Continue with Google
      </Button>
    </div>
  );
} 